import { DatePickerInput } from '@creditornot/cb-date-picker';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import TimePicker from 'common/components/weekly-schedules/TimePairInput/TimePicker';

type Props = {
  onChange: (value: string | null) => void;
  value: string | null;
  displayUtcHint?: boolean;
};

const Root = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;

const HintContainer = styled.div`
  ${typographyCss.Small()};
  color: ${cbSemanticCssVars.textSecondary};
  margin-block-start: 4px;
`;

const StyledTimePicker = styled(TimePicker)`
  width: 64px;
  margin-inline-start: 8px;
`;

const ScheduleDatePicker = ({ onChange, value, displayUtcHint = false }: Props) => {
  const intl = useIntl();

  const initialValues = useMemo(() => {
    const valueAsDateTime = value ? DateTime.fromISO(value) : null;

    return {
      dateValue: valueAsDateTime ? valueAsDateTime.toJSDate() : undefined,
      timeValue: valueAsDateTime ? valueAsDateTime.hour * 60 + valueAsDateTime.minute : 0,
      inputValue: valueAsDateTime ? valueAsDateTime.toLocaleString(DateTime.DATETIME_SHORT) : '',
    };
  }, [value]);

  useEffect(() => {
    setDateValue(initialValues.dateValue);
    if (initialValues.dateValue) {
      setTimeValue(initialValues.timeValue);
    }
  }, [initialValues]);

  const [dateValue, setDateValue] = useState<Date | undefined>(initialValues.dateValue);
  const [timeValue, setTimeValue] = useState<number>(initialValues.timeValue);

  const handleTimeValueChange = useCallback(
    (val: number) => {
      setTimeValue(val);
      if (dateValue) {
        const date = DateTime.fromJSDate(dateValue, {}).startOf('day');

        const combinedDate = date.plus({ minutes: val });

        onChange(combinedDate.toISO());
      }
    },
    [dateValue, onChange],
  );

  const handleDateValueChange = useCallback(
    (val: Date | undefined) => {
      setDateValue(val);
      if (val) {
        const date = DateTime.fromJSDate(val).startOf('day');

        const combinedDate = date.plus({ minutes: timeValue });

        onChange(combinedDate.toISO());
      } else {
        onChange(null);
      }
    },
    [onChange, timeValue],
  );

  const utcDate = useMemo(() => {
    if (!dateValue || !displayUtcHint) {
      return;
    }

    const date = DateTime.fromJSDate(dateValue, {}).startOf('day');
    const combinedDate = date.plus({ minutes: timeValue });

    return combinedDate.toUTC().toLocaleString({
      dateStyle: 'short',
      timeStyle: 'short',
    });
  }, [dateValue, displayUtcHint, timeValue]);

  return (
    <>
      <Root>
        <DatePickerInput
          placeholder={intl.formatMessage({ id: 'date-time.date-picker-placeholder' })}
          disabledDays={{
            before: DateTime.now().toJSDate(),
          }}
          firstDayOfWeek={1}
          value={dateValue}
          onChange={handleDateValueChange}
          closeOnChange
          popoverZIndex={100002}
        />
        <StyledTimePicker value={timeValue} onChange={handleTimeValueChange} />
      </Root>
      {displayUtcHint && !!utcDate && (
        <HintContainer>
          <FormattedMessage id="shared-menu.dates.utc-hint" values={{ utcDate }} />
        </HintContainer>
      )}
    </>
  );
};

export default ScheduleDatePicker;
